import React from "react";
import { Card } from "@/components/ui/card";

const MarginAnalysis = ({ result, selectedAdjustment, onSelectAdjustment }) => {
  if (!result || result.total_linear_feet === 0) {
    return null;
  }

  // Calculate what price adjustment is needed to hit target margins
  const calculatePriceForMargin = (targetMargin) => {
    // Formula: Revenue * Margin = Profit
    // Revenue = Cost / (1 - Margin)
    // Price Adjustment = New Revenue - Current Revenue
    const cost = result.total_cost;
    const targetRevenue = cost / (1 - targetMargin / 100);
    const priceAdjustment = (targetRevenue - result.total_extended_price) / result.total_linear_feet;
    
    return priceAdjustment;
  };

  // Current margin and scenarios
  const currentMargin = result.overall_margin_percent;
  
  const scenarios = [
    { label: "Aggressive (45% Margin)", targetMargin: 45 },
    { label: "Competitive (60% Margin)", targetMargin: 60 },
    { label: "Current Bid", targetMargin: currentMargin, isCurrent: true },
    { label: "Conservative (70% Margin)", targetMargin: 70 },
    { label: "Premium (80% Margin)", targetMargin: 80 },
  ];

  const calculateScenario = (targetMargin) => {
    const priceAdjustment = calculatePriceForMargin(targetMargin);
    const newRevenue = result.total_extended_price + (priceAdjustment * result.total_linear_feet);
    const newProfit = result.total_profit + (priceAdjustment * result.total_linear_feet);
    const newMargin = (newProfit / newRevenue) * 100;
    const newDailyProfit = newProfit / result.production_days;
    const pricePerLF = newRevenue / result.total_linear_feet;
    
    return {
      revenue: newRevenue,
      profit: newProfit,
      margin: newMargin,
      dailyProfit: newDailyProfit,
      priceAdjustment: priceAdjustment,
      pricePerLF: pricePerLF
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getMarginColor = (margin) => {
    if (margin >= 70) return "text-[#10b981]";
    if (margin >= 60) return "text-[#22c55e]";
    if (margin >= 50) return "text-[#84cc16]";
    if (margin >= 40) return "text-[#eab308]";
    return "text-[#f97316]";
  };

  return (
    <Card className="millmetrix-card p-6" data-testid="margin-analysis">
      <div className="section-header">
        <h3 className="text-xl font-semibold tracking-tight uppercase text-muted-foreground">
          Margin Analysis
        </h3>
      </div>
      
      <p className="text-sm text-[#94a3b8] mb-4">
        Explore different pricing strategies to optimize margin vs. volume
      </p>

      <div className="space-y-2">
        {scenarios.map((scenario, idx) => {
          const calc = calculateScenario(scenario.targetMargin);
          const isCurrent = scenario.isCurrent;
          const isSelected = Math.abs(selectedAdjustment - calc.priceAdjustment) < 0.001;
          
          return (
            <div
              key={idx}
              onClick={() => onSelectAdjustment(calc.priceAdjustment)}
              className={`p-3 rounded-md transition-all cursor-pointer ${
                isSelected
                  ? "bg-[#1e293b] border-2 border-[#f97316] shadow-lg shadow-[#f97316]/20" 
                  : "bg-[#0f172a] border border-[rgba(255,255,255,0.05)] hover:border-[#f97316]/50 hover:bg-[#1e293b]/50"
              }`}
              data-testid={`scenario-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-[#e2e8f0]">
                    {scenario.label}
                    {isSelected && <span className="ml-2 text-xs text-[#f97316]">✓ SELECTED</span>}
                  </div>
                  {!isCurrent && (
                    <div className="text-xs text-[#cbd5e1]">
                      {calc.priceAdjustment > 0 ? '+' : ''}{calc.priceAdjustment.toFixed(2)}/LF · {calc.priceAdjustment > 0 ? '+' : ''}{formatCurrency(calc.priceAdjustment * result.total_linear_feet)} total
                    </div>
                  )}
                  {isCurrent && (
                    <div className="text-xs text-[#10b981]">
                      ${calc.pricePerLF.toFixed(2)}/LF
                    </div>
                  )}
                </div>
                <div className={`text-2xl font-mono font-bold tabular-nums ${getMarginColor(calc.margin)}`}>
                  {calc.margin.toFixed(1)}%
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-[#cbd5e1]">Revenue: </span>
                  <span className="font-mono font-semibold text-[#e2e8f0]">{formatCurrency(calc.revenue)}</span>
                </div>
                <div>
                  <span className="text-[#94a3b8]">Profit: </span>
                  <span className="font-mono font-semibold text-[#10b981]">{formatCurrency(calc.profit)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[#94a3b8]">Daily Profit: </span>
                  <span className="font-mono font-semibold text-[#10b981]">{formatCurrency(calc.dailyProfit)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-[#1e293b] rounded-md border border-[rgba(249,115,22,0.2)]">
        <div className="text-xs font-bold uppercase tracking-widest text-[#f97316] mb-2">
          Strategy Insight
        </div>
        <p className="text-xs text-[#94a3b8] leading-relaxed">
          Each scenario shows the price adjustment needed to hit the target margin. 
          The adjustment scales with your costs - higher cost jobs get proportionally larger adjustments, maintaining consistent margins across all project sizes.
        </p>
      </div>
    </Card>
  );
};

export default MarginAnalysis;
